import React from "react";
import { Box, Chip, Grid, styled, Typography } from "@mui/material";
import { CenteredSiteBlock } from "../../routes/Home";
import { PlayArrow } from "@mui/icons-material";
import { ReactComponent as HeadphonesIcon } from "../../resources/images/svg/home/podcasts/headphonesWhite.svg";
import moment from "moment";
import InexButton from "../InexButton";
import { useIsMobile } from "../../hooks/responsive";

const Container = styled(Box)(({ theme, backgroundImage }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    minHeight: "100px",
    background: `url(${backgroundImage})`,
    // backdropFilter: "blur(25px)",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    position: "relative",
  };
});

const BlurContainer = styled(Box)(({ theme }) => {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    width: "100%",
    minHeight: "100px",
    backgroundColor: theme.palette.blackPro + "aa",
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    position: "relative",
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(5),
    "-webkit-backdrop-filter": "blur(10px)",
    "backdrop-filter": "blur(10px)",
  };
});

const Header = ({
  podcast,
  videoItems,
  onClickWatch,
  onClickListen,
  onClickDownloadTranscript,
  isDownloadingTranscript = false,
  ...props
}) => {
  const isMobile = useIsMobile();
  const uploadDate = React.useMemo(
    () => moment(podcast?.dateSortie),
    [podcast],
  );
  const canDownloadTranscript = React.useMemo(() => {
    return String(podcast?.transcription || "").trim().length >= 10;
  }, [podcast]);

  if (!podcast) return null;

  return (
    <Container
      backgroundImage={`https://medias.inrees.com/img/pro/audiovisuel/home_${podcast?.id}.jpg`}
    >
      <BlurContainer>
        <CenteredSiteBlock
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            paddingRight: 2,
            paddingLeft: 2,
          }}
        >
          <Typography
            variant="h2"
            sx={{
              fontWeight: 500,
              margin: 0,
              textAlign: "left",
              color: "white",
              width: "100%",
            }}
          >
            {podcast?.titre}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              marginTop: 1,
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                color: "white",
                fontWeight: 800,
              }}
            >
              {uploadDate.format("[Sortie le ]DD/MM/YYYY")}
            </Typography>
            <Chip
              label={`${videoItems && videoItems.length > 0 ? videoItems[0].dureeMin : 0} min.`}
              sx={{
                backgroundColor: "white",
                fontWeight: 900,
              }}
            ></Chip>
          </Box>
          <Grid
            container
            mt={2}
            spacing={isMobile ? 0 : 4}
            sx={{
              width: "100%",
            }}
          >
            <Grid
              item
              size={{
                xs: 12,
                md: 8,
              }}
            >
              <Box
                onClick={() => {
                  onClickWatch();
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  aspectRatio: 885 / 465,
                  backgroundImage: `url(https://medias.inrees.com/img/pro/audiovisuel/home_${podcast?.id}.jpg)`,
                  backgroundRepeat: "no-repeat",
                  backgroundSize: "cover",
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "scale(1.01)",
                    cursor: "pointer",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: (theme) => theme.palette.primary.main,
                    borderRadius: "50%",
                    width: "85px",
                    height: "85px",
                    "box-shadow": "0px 4px 35.8px 0px #000000BD",
                    transition: "background-color 0.3s",
                    "&:hover": {
                      backgroundColor: (theme) => theme.palette.primary.light,
                      cursor: "pointer",
                    },
                  }}
                >
                  <PlayArrow
                    sx={{
                      color: "white",
                      width: "70px",
                      height: "70px",
                    }}
                  />
                </Box>
              </Box>
            </Grid>
            <Grid
              item
              size={{
                xs: 12,
                md: 4,
              }}
            >
              <Box
                onClick={() => {
                  onClickListen();
                }}
                sx={{
                  width: "100%",
                  height: "100%",
                  backgroundColor: (theme) => theme.palette.blackPro + "77",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "transform 0.3s, background-color 0.3s",
                  paddingTop: 2,
                  paddingBottom: 2,
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.blackPro + "55",
                    transform: "scale(1.01)",
                    cursor: "pointer",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 900,
                    maxWidth: "50%",
                    color: "white",
                    textAlign: "center",
                  }}
                >
                  Ce podcast est aussi disponible en audio
                </Typography>
                <HeadphonesIcon
                  style={{
                    marginTop: "20px",
                    stroke: "white !important",
                    width: "45px",
                    height: "45px",
                  }}
                />
                <InexButton
                  text={"Écouter le podcast"}
                  variant={"text"}
                  textWithGradient={false}
                  onClick={(event) => {
                    event.stopPropagation();
                    onClickListen();
                  }}
                  sx={{
                    zIndex: 1,
                    marginTop: 1.5,
                    width: "240px",
                    height: "50px",
                    background: (theme) => theme.palette.primary.main,
                    color: "white",
                    "&:hover": {
                      backgroundColor: "white",
                      "& > p": {
                        color: (theme) => theme.palette.primary.main,
                      },
                    },
                  }}
                />
                {canDownloadTranscript && (
                  <InexButton
                    text={"Télécharger la transcription"}
                    variant={"text"}
                    textWithGradient={false}
                    loading={isDownloadingTranscript}
                    loadingColor={"white"}
                    onClick={(event) => {
                      event.stopPropagation();
                      onClickDownloadTranscript?.();
                    }}
                    sx={{
                      zIndex: 1,
                      marginTop: 1.5,
                      width: "240px",
                      height: "50px",
                      background: "transparent",
                      color: "white",
                      border: "1px solid rgba(255,255,255,0.8)",
                      "& > p": {
                        color: "white",
                      },
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.12)",
                        border: "1px solid white",
                        "& > p": {
                          color: "white",
                          background: "none",
                          WebkitBackgroundClip: "initial",
                          WebkitTextFillColor: "initial",
                        },
                      },
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </CenteredSiteBlock>
      </BlurContainer>
    </Container>
  );
};

export default Header;
